import { Link } from "react-router-dom";
import { CheckCircle2, Clock } from "lucide-react";
import { PageLoader } from "#/components/common/PageLoader";
import { ErrorState } from "#/components/common/ErrorState";
import { EmptyState } from "#/components/common/EmptyState";
import { useAllTrainingRequests } from "../hooks";

function fmt(v?: string | null) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return v; }
}

function fmtDateTime(v?: string | null) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return v; }
}

export default function TrainingAttendancePage() {
  // Attendance = requests that are COMPLETED (training happened) or APPROVED with a past end date
  const completedQuery = useAllTrainingRequests(0, 100, "COMPLETED");
  const approvedQuery  = useAllTrainingRequests(0, 100, "APPROVED");

  if (completedQuery.isLoading || approvedQuery.isLoading) return <PageLoader label="Loading attendance…" />;
  if (completedQuery.isError) return <ErrorState title="Failed to load" message={String(completedQuery.error)} />;

  const today = new Date().toISOString().slice(0, 10);

  const completed = completedQuery.data?.content ?? [];
  const inProgress = (approvedQuery.data?.content ?? []).filter(
    (r) => r.trainingStartDate && r.trainingStartDate <= today &&
           (!r.trainingEndDate || r.trainingEndDate >= today),
  );

  const all = [...inProgress, ...completed];

  if (all.length === 0) {
    return (
      <div className="space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Attendance</h1>
          </div>
        <EmptyState title="No attendance records" description="Completed and ongoing training sessions will appear here." />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Attendance</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          {inProgress.length > 0 && `${inProgress.length} in progress · `}
          {completed.length} completed
        </p>
      </div>

      <div className="rounded-2xl border bg-white shadow-sm overflow-hidden divide-y">
        {all.map((r) => {
          const isInProgress = r.status === "APPROVED";
          return (
            <Link
              key={r.id}
              to={`/hr/training/${r.id}`}
              className="flex flex-wrap items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors"
            >
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                isInProgress ? "bg-amber-100 text-amber-600" : "bg-emerald-100 text-emerald-600"
              }`}>
                {isInProgress ? <Clock className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
              </div>

              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-800 text-sm truncate">{r.trainingTitle}</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {r.employeeName}
                  {r.categoryName && ` · ${r.categoryName}`}
                  {r.providerName && ` · ${r.providerName}`}
                </p>
              </div>

              <div className="text-right text-xs shrink-0">
                <span className={`rounded-full px-2 py-0.5 font-semibold ${
                  isInProgress ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"
                }`}>
                  {isInProgress ? "In Progress" : "Completed"}
                </span>
                <p className="text-slate-400 mt-1">
                  {fmt(r.trainingStartDate)}{r.trainingEndDate ? ` → ${fmt(r.trainingEndDate)}` : ""}
                </p>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
